import { useEffect, useState } from 'react';
import { useABTestDispatcher, useABTestExperiment, setTestVariant } from 'src/context/ab-test';
import { Experiment, executeExperiment, getActiveVariationForExperiment } from 'utils/convert';
import useConvert from './useConvert';

/**
 * Sets up an specific experiment to run.
 * @param experimentName The name of the experiment to run
 * @returns {Object}
 * @property {ABTestExperiment | null} experiment The experiment that was initialized
 * @property {boolean} isConvertLoading Whether convert is still loading or not. Useful for displaying spinners while you wait for experiment data.
 * @property {boolean} isConvertInitialized Whether convert successfully loaded or not.
 * @property {function(Experiment):void} executeExperiment Included for convenience. Imported from utils/convert.
 */
export default function useConvertExperiment(experimentName: Experiment) {
  const { isLoading: isConvertLoading, isInitialized: isConvertInitialized } = useConvert();
  const abTestDispatch = useABTestDispatcher();
  const [experimentDispatched, setExperimentDispatched] = useState(false);
  const experiment = useABTestExperiment(experimentName);

  // To avoid context dispatch rendering recursion, we dispatch inside useEffect
  useEffect(() => {
    if (isConvertInitialized && !experimentDispatched) {
      const variant = getActiveVariationForExperiment(experimentName);
      const action = setTestVariant(experimentName, variant);
      abTestDispatch?.(action);
      setExperimentDispatched(true);
    }
  }, [
    isConvertInitialized,
    experimentName,
    experimentDispatched,
    setExperimentDispatched,
    abTestDispatch,
  ]);

  return { experiment, isConvertLoading, isConvertInitialized, executeExperiment };
}
